import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { GoClock } from "react-icons/go";
import { HiOutlineUsers } from "react-icons/hi";
import { Link } from "react-router-dom";
import Menu from "../Menu";
import {
  getRestaurantSlots,
  bookTable,
  getOngoingTableRequest,
} from "../../../../../api/user/restaurants";
import { useQuery, useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useLocationData } from "../../../../../context/locationContext";
import { useTranslation } from "../../../../../hooks/useTranslation";

// Popup Modal Component
const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] sm:max-w-[90%] sm:w-[579px] relative sm:shadow-lg py-[26px] px-[21px]">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const BooKTable = ({ restaurantId, restaurant }) => {
  // const slots = [
  //   "12:00PM",
  //   "12:30PM",
  //   "1:00PM",
  //   "1:30PM",
  //   "2:30PM",
  //   "3:00PM",
  //   "3:30PM",
  //   "4:00PM",
  //   "4:30PM",
  //   "5:00PM",
  //   "5:30PM",
  // ];
  const { t } = useTranslation();
  const [selectedData, setSelectedData] = useState({
    bookingDate: "",
    mealType: "breakfast",
    slot: "",
    restaurantId,
    totalGuest: 1,
    foodType: "veg",
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["restaurant-slots", restaurantId],
    queryFn: () => getRestaurantSlots(restaurantId),
    keepPreviousData: true,
    enabled: !!restaurantId,
  });

  const { data: tableBooking } = useQuery({
    queryKey: ["table-request", restaurantId],
    queryFn: () => getOngoingTableRequest(restaurantId),
    keepPreviousData: true,
    enabled: !!restaurantId,
  });

  const slots = data?.data?.slots;
  const booking = tableBooking?.data?.ongoingReservation;
  const { userLocation } = useLocationData();

  const bookTableMutation = useMutation({
    mutationFn: (payload) => bookTable(payload),
    onSuccess: () => {
      setIsPopupOpen(true);
      setSelectedData({
        bookingDate: "",
        mealType: "breakfast",
        slot: "",
        restaurantId,
        totalGuest: 1,
        foodType: "veg",
      })
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || t("booktable").locationError;
      toast.error(errorMessage);
    },
  });


  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hourStr, minute] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSlotSelect = (slotId) => {
    setSelectedData((prev) => ({
      ...prev,
      slot: slotId,
    }));
  };

  const handlePlaceOrder = () => {
    return new Promise((resolve, reject) => {
      if (!userLocation) {
        toast.error(t("booktable").locationError);
        return reject();
      }

      const restaurantCoords = restaurant?.location?.coordinates;
      if (!restaurantCoords || restaurantCoords.length !== 2) {
        toast.error(t("booktable").restaurantLocationError);
        return reject();
      }

      const distance = getDistanceFromLatLonInKm(
        userLocation.latitude,
        userLocation.longitude,
        restaurantCoords[1],
        restaurantCoords[0]
      );

      if (distance > 20) {
        toast.error(t("booktable").distanceError);
        return reject();
      }

      resolve();
    });
  };

  const handleBooking = async () => {
    try {
      await handlePlaceOrder();
    } catch {
      return;
    }
    const { bookingDate, mealType, slot, restaurantId, totalGuest, foodType } =
      selectedData;

    if (!bookingDate) {
      toast.error(t("booktable").selectDateError);
      return;
    }

    if (!mealType) {
      toast.error(t("booktable").selectMealError);
      return;
    }

    if (!totalGuest || totalGuest < 1) {
      toast.error(t("booktable").selectGuestsError);
      return;
    }

    if (!foodType) {
      toast.error(t("booktable").selectFoodError);
      return;
    }

    if (!slot) {
      toast.error(t("booktable").selectSlotError);
      return;
    }

    // ✅ Check if selected slot time is in the past
    const selectedSlot = slots.find((s) => s._id === slot);
    if (selectedSlot) {
      const [hours, minutes] = selectedSlot.startTime.split(":");
      const slotDateTime = new Date(bookingDate);
      slotDateTime.setHours(+hours, +minutes, 0, 0);

      if (slotDateTime < new Date()) {
        toast.error(t("booktable").slotUnavailableError);
        return;
      }
    }

    bookTableMutation.mutate(selectedData);
  };

  function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  return (
    <>
      <div className="">
        <h2 className="text-[22px] font-medium text-black mb-5">
          {t("booktable").selectBookingDetails}
        </h2>
        <div className="flex gap-5 flex-wrap">
          <div className="flex items-center gap-[10px] border border-[#DDDDDD] px-[13px] py-[7px] rounded-[5px] text-sm text-[#686868]">
            <input
              type="date"
              name="bookingDate"
              value={selectedData.bookingDate}
              onChange={handleInputChange}
              className="bg-transparent focus:outline-none"
              min={new Date().toISOString().split("T")[0]}
            />
          </div>

          <div className="flex items-center gap-[10px] border border-[#DDDDDD] px-[13px] py-[7px] rounded-[5px] text-sm text-[#686868]">
            <HiOutlineUsers size={18} className="text-[#686868]" />
            <select
              name="totalGuest"
              value={selectedData.totalGuest}
              onChange={handleInputChange}
              className="bg-transparent focus:outline-none  min-w-[100px]"
            >
              {Array.from({ length: 20 }, (_, i) => (
                <option key={i} value={i + 1}>
                  {i + 1} {t("booktable").guests}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-[10px] border border-[#DDDDDD] px-[13px] py-[7px] rounded-[5px] text-sm text-[#686868]">
            <GoClock size={18} className="text-[#686868]" />
            <select
              name="mealType"
              value={selectedData.mealType}
              onChange={handleInputChange}
              className="bg-transparent focus:outline-none  min-w-[100px]"
            >
              <option value="breakfast">{t("booktable").breakfast}</option>
              <option value="lunch">{t("booktable").lunch}</option>
              <option value="dinner">{t("booktable").dinner}</option>
            </select>
          </div>

          <div className="flex items-center gap-[10px] border border-[#DDDDDD] px-[13px] py-[7px] rounded-[5px] text-sm text-[#686868]">
            <select
              name="foodType"
              value={selectedData.foodType}
              onChange={handleInputChange}
              className="bg-transparent focus:outline-none min-w-[100px]"
            >
              <option value="veg">{t("booktable").veg}</option>
              <option value="non-veg">{t("booktable").nonVeg}</option>

              <option value="both">{t("booktable").both}</option>
            </select>
          </div>
        </div>

        <div className="border border-[#DDDDDD] rounded-md p-[15px] pb-[31px] mt-[30px]">
          <h3 className="mb-[15px] text-[20px] font-medium">
            {t("booktable").selectSlot}
          </h3>
          <div className="flex flex-wrap gap-[15px]">
            {slots?.map((slot) => {
              // Combine selected booking date and slot time
              const selectedDate = new Date(selectedData.bookingDate);
              const [hours, minutes] = slot?.startTime.split(":");
              selectedDate.setHours(+hours, +minutes, 0, 0);

              // Check if slot is in the past
              const isPast = selectedDate < new Date();

              return (
                <button
                  key={slot?._id}
                  onClick={() => !isPast && handleSlotSelect(slot?._id)}
                  disabled={isPast}
                  className={`px-[10px] py-[4px] rounded-[5px] text-base border transition
            ${selectedData.slot === slot?._id
                      ? "bg-[#4CAF50] text-white"
                      : isPast
                        ? "bg-gray-100 text-gray-400 border-[#DDDDDD] cursor-not-allowed"
                        : "bg-white text-[#737373] border-[#DDDDDD] hover:bg-gray-100"
                    }`}
                >
                  {formatTime(slot?.startTime)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end md:mt-[56px] mt-5 w-full">
          <button
            className="md:w-auto w-full rounded-[2px] bg-[#4CAF50] 2xl:text-lg text-base text-white text-center font-bold 2xl:py-[14px] 2xl:px-[28px] p-3 green-shadow hover:bg-[#209b25]"
            onClick={handleBooking}
          >
            {t("booktable").bookNow}
          </button>
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full max-w-[443px] mx-auto text-center">
          <FaCheckCircle size={32} color="#027D07" className="mx-auto" />
          <h2 className="sm:text-[17px] tex-base leading-[26px] text-center font-medium mb-[30px] sm:leading-[20px] text-black mt-[25px]">
            {t("booktable").bookingSuccessMessage}
          </h2>
        </div>
      </PopupModal>
    </>
  );
};

export default BooKTable;
